import { useQuery } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { FetchCaseDocsRes } from '@/api/types/queries';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export const useGetDocs = ({
  caseId,
  page,
  pageSize,
  searchQuery,
  processed,
  extensions,
  startDate,
  endDate,
  documentTypes,
  documentThemes,
  hasKeyFacts,
  hasRelevantFacts,
  runId,
  sortBy,
  sortOrder,
}: {
  caseId: string | null;
  page: number;
  pageSize: number;
  searchQuery: string;
  processed?: boolean | null;
  extensions?: string[];
  startDate?: string | null;
  endDate?: string | null;
  documentTypes?: string[];
  documentThemes?: string[];
  hasKeyFacts?: boolean | null;
  hasRelevantFacts?: boolean | null;
  runId?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}) => {
  const { fetchConfigGET } = useGetFetchConfig();

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    params.append('searchQuery', searchQuery);

    if (processed !== null && processed !== undefined) {
      params.append('processed', processed.toString());
    }

    if (extensions && extensions.length > 0) {
      extensions.forEach((ext) => params.append('extensions[]', ext));
    }

    if (startDate) {
      params.append('startDate', startDate);
    }

    if (endDate) {
      params.append('endDate', endDate);
    }

    if (documentTypes && documentTypes.length > 0) {
      documentTypes.forEach((type) => params.append('documentTypes[]', type));
    }

    if (documentThemes && documentThemes.length > 0) {
      documentThemes.forEach((theme) => params.append('documentThemes[]', theme));
    }

    if (hasKeyFacts !== null && hasKeyFacts !== undefined) {
      params.append('hasKeyFacts', hasKeyFacts.toString());
    }

    if (hasRelevantFacts !== null && hasRelevantFacts !== undefined) {
      params.append('hasRelevantFacts', hasRelevantFacts.toString());
    }

    if (sortBy) {
      params.append('sortBy', sortBy);
    }

    if (sortOrder) {
      params.append('sortOrder', sortOrder);
    }

    if (runId) {
      params.append('runId', runId);
    }

    return params.toString();
  };

  return useQuery<FetchCaseDocsRes>(
    [
      QueryEntity.CaseDocs,
      { caseId },
      {
        page,
        pageSize,
        searchQuery,
        processed,
        extensions,
        startDate,
        endDate,
        sortBy,
        sortOrder,
        documentTypes,
        documentThemes,
        hasKeyFacts,
        hasRelevantFacts,
        runId,
      },
    ],
    () => {
      return fetch(`${APIBaseChronos}/api/case/${caseId}/docs?${buildQueryParams()}`, fetchConfigGET).then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json();
      });
    },
    { refetchOnWindowFocus: false, enabled: !!caseId },
  );
};
